import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Spinner } from "reactstrap";

const POLL_MS = 3000;
const SHOWN = 3;
const pct = (value) => (value == null ? "—" : `${Math.round(value)}%`);

// Discover issues, merge equivalent issues, then classify each comment.
// Runs on request and saves the result per filter set for the day.
export default function FeedbackThemes({ apiClient, request, onLook }) {
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState("");
  const timer = useRef(null);
  const current = useRef(null);
  const requestRef = useRef(request);
  requestRef.current = request;

  // New filters mean a different theme run.
  useEffect(() => {
    current.current = null;
    setJob(null);
    setError("");
    return () => { current.current = null; clearTimeout(timer.current); };
  }, [request]);

  const poll = (analysisId) => {
    timer.current = setTimeout(() => {
      apiClient.JMPost("/v1/notes/feedback/themes/status", { analysisId }).then((response) => {
        if (current.current !== analysisId) return;
        if (response?.error) throw new Error(response.error);
        setJob(response);
        if (response.status === "queued" || response.status === "running") poll(analysisId);
      }).catch(() => { if (current.current === analysisId) setError("Connection interrupted. Retry to check this theme run again."); });
    }, POLL_MS);
  };

  const start = () => {
    clearTimeout(timer.current);
    setError("");
    if (job && ["queued", "running"].includes(job.status)) {
      poll(job.analysisId);
      return;
    }
    setStarting(true);
    const asked = request;
    apiClient.JMPost("/v1/notes/feedback/themes/start", request).then((response) => {
      if (asked !== requestRef.current) return;
      if (response?.error) throw new Error(response.error);
      current.current = response.analysisId;
      setJob(response);
      if (response.status === "queued" || response.status === "running") poll(response.analysisId);
    }).catch(() => { if (asked === requestRef.current) setError("Unable to start the theme run. Try again."); })
      .finally(() => setStarting(false));
  };

  const result = job?.status === "completed" ? job.result : null;
  const running = !error && (starting || job?.status === "queued" || job?.status === "running");
  const failed = job?.status === "failed";

  return (
    <div className="note-intel-panel fb-panel fb-compact-panel">
      <div className="note-intel-panel-header">
        <div>
          <h3>Top themes from “Tell us more”</h3>
          <p>
            AI-grouped themes from what PTs wrote.
            {result && ` ${result.totalComments.toLocaleString()} comments in these filters.`}
            {result?.themes.length ? " Click one to read the comments." : ""}
          </p>
        </div>
        {!running && !result && (
          <Button size="sm" color="primary" outline onClick={start}>
            {failed || error ? "Try again" : "Find themes"}
          </Button>
        )}
        {result && <span className="fb-muted">Saved for today</span>}
      </div>

      {error && <Alert color="danger">{error}</Alert>}
      {failed && <Alert color="danger">{job.error || "Unable to find themes."}</Alert>}
      {running && <div className="fb-loading"><Spinner size="sm" /> Finding themes… this can take a few minutes.</div>}
      {!job && !running && !error && (
        <p className="fb-muted">Finds specific issues in the comments, combines equivalent issues, then checks which comments match each theme. Results are saved for today.</p>
      )}

      {result && (result.themes.length
        ? <>
            <div className="fb-card-grid">
              {result.themes.slice(0, SHOWN).map((theme, index) => (
                <button type="button" key={theme.name} className="fb-theme-card" title={theme.definition}
                  onClick={() => onLook({
                    keys: theme.keys,
                    title: theme.name,
                    note: theme.keys.length < theme.comments
                      ? `The ${theme.keys.length} newest of ${theme.comments.toLocaleString()} comments`
                      : undefined,
                  })}>
                  <span className="fb-rank">#{index + 1} THEME</span>
                  <strong>{theme.name} ›</strong>
                  <span className="fb-muted">
                    {theme.comments.toLocaleString()} matching comments · {pct(theme.pct)} of {result.analyzedComments < result.totalComments ? "analyzed" : "all"} comments
                  </span>
                  <blockquote className="fb-quote">“{theme.quote}”</blockquote>
                </button>
              ))}
            </div>
          </>
        : <p className="fb-muted">No recurring actionable themes found in these filters yet.</p>)}
      {result && <>
        <p className="fb-footnote">Themes need at least two comments. A comment can match more than one theme, so percentages may overlap. AI assignments may be imperfect.</p>
        {result.analyzedComments < result.totalComments && (
          <p className="fb-footnote">Analyzed the newest {result.analyzedComments.toLocaleString()} of {result.totalComments.toLocaleString()} comments.</p>
        )}
        {[["vague", "Too vague to classify"], ["no_match", "No matching theme"]].map(([reason, label]) => {
          const group = result.unassigned?.[reason];
          return group?.comments > 0 && <Button key={reason} size="sm" color="link" onClick={() => onLook({
            keys: group.keys, title: label,
            note: group.keys.length < group.comments
              ? `The ${group.keys.length} newest of ${group.comments.toLocaleString()} comments`
              : undefined,
          })}>{label}: {group.comments.toLocaleString()}</Button>;
        })}
      </>}

    </div>
  );
}
